/**
 * Retail Warehouse Property Type Handler
 * Combination of retail and warehouse characteristics
 */

import { PropertyType } from '../../../shared/definitions/property';
import { CommercialPropertyHandler } from '../base/commercial-property-handler.abstract';
import { RoomTypeConfig, FeatureConfig, FeatureCategory } from '@core/services/property-analysis/property-room-config.service';

export class RetailWarehouseHandler extends CommercialPropertyHandler {
  readonly propertyType = PropertyType.RetailWarehouse;
  readonly displayName = 'Retail Warehouse';
  readonly pluralName = 'Retail Warehouses';
  readonly icon = 'store';
  readonly description = 'Large retail space with warehouse/storage areas';

  getRoomTypes(): RoomTypeConfig[] {
    return [
      {
        typeKey: 'retail_area',
        label: 'Retail Area',
        pluralLabel: 'Retail Areas',
        countField: 'retailAreas',
        icon: 'storefront',
        defaultCount: 0,
        description: 'Customer-facing retail space',
        formFields: [
          { name: 'name', label: 'Area Name', type: 'text', placeholder: 'e.g., Main Showroom' },
          { name: 'sqm', label: 'Retail Area', type: 'number', suffix: 'm²', required: true, min: 50 },
          { name: 'ceilingHeight', label: 'Ceiling Height', type: 'number', suffix: 'm', min: 3 },
          { name: 'hasDisplayArea', label: 'Display Area', type: 'checkbox', default: false },
          { name: 'notes', label: 'Notes', type: 'textarea' }
        ]
      },
      {
        typeKey: 'warehouse_storage',
        label: 'Warehouse Storage',
        pluralLabel: 'Warehouse Storage Areas',
        countField: 'warehouseAreas',
        icon: 'inventory',
        defaultCount: 0,
        description: 'Back-of-house warehouse and storage',
        formFields: [
          { name: 'name', label: 'Area Name', type: 'text', placeholder: 'e.g., Main Warehouse' },
          { name: 'sqm', label: 'Warehouse Area', type: 'number', suffix: 'm²', required: true, min: 50 },
          { name: 'ceilingHeight', label: 'Ceiling Height', type: 'number', suffix: 'm', min: 4 },
          { name: 'floorLoadCapacity', label: 'Floor Load Capacity', type: 'number', suffix: 'kg/m²', min: 500 },
          { name: 'hasRacking', label: 'Has Racking System', type: 'checkbox', default: false },
          { name: 'notes', label: 'Notes', type: 'textarea' }
        ]
      }
    ];
  }

  getFeatures(): FeatureConfig[] {
    const coreFeatures = this.getCoreCommercialFeatures();
    
    const retailWarehouseFeatures: FeatureConfig[] = [
      {
        key: 'loading_dock',
        label: 'Loading Dock',
        icon: 'local_shipping',
        category: FeatureCategory.FLEXIBLE,
        description: 'Loading dock for deliveries',
        detailsInterface: 'LoadingDockDetail',
        formFields: [
          { name: 'numberOfDocks', label: 'Number of Docks', type: 'number', required: true, min: 1 },
          { name: 'dockHeight', label: 'Dock Height', type: 'number', suffix: 'm', min: 0.8, max: 2.0 },
          { name: 'hasDockLeveler', label: 'Dock Leveler', type: 'checkbox', default: false }
        ]
      },
      {
        key: 'large_parking',
        label: 'Large Parking Area',
        icon: 'local_parking',
        category: FeatureCategory.FLEXIBLE,
        description: 'Extensive customer and staff parking',
        detailsInterface: 'ParkingDetail',
        formFields: [
          { name: 'customerSpaces', label: 'Customer Parking Spaces', type: 'number', required: true, min: 10 },
          { name: 'staffSpaces', label: 'Staff Parking Spaces', type: 'number', min: 0 },
          { name: 'hasDisabledSpaces', label: 'Disabled Parking', type: 'checkbox', default: true }
        ]
      }
    ];

    return [...coreFeatures, ...retailWarehouseFeatures];
  }

  override getMainCharacteristics(): string[] {
    return ['retailArea', 'warehouseArea', 'ceilingHeight', 'loadingDocks', 'customerParkingSpaces'];
  }

  override getRecommendedMinSurface(): number {
    return 200;
  }

  override getPriceRangeMultiplier(): number {
    return 0.8;
  }
}

